import { useState } from 'react';
import { toast } from 'react-toastify';
import { colors, layout } from '../NewHire/v2/styles';
import AnnualReviewService from '../../../services/AnnualReview';
import FileInput from '../NewHire/v2/components/FileInput';
import { AnnualReviewResponse } from './types';

interface UploadSignedModalProps {
  review: AnnualReviewResponse;
  token: string;
  onClose: () => void;
  onEditGrowthPlan: () => void;
  onUploaded: (updated: AnnualReviewResponse) => void;
}

const secondaryAction = {
  background: 'transparent',
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 12,
  color: colors.muted,
} as const;

const UploadSignedModal = ({
  review: initialReview,
  token,
  onClose,
  onEditGrowthPlan,
  onUploaded,
}: UploadSignedModalProps) => {
  const [review, setReview] = useState<AnnualReviewResponse>(initialReview);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await AnnualReviewService(token).regenerateDraftPdf(review.id);
      setReview(res.data);
      toast.success('Draft PDF regenerated.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not regenerate draft PDF');
    } finally {
      setRegenerating(false);
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error('Please select a signed PDF first.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      const res = await AnnualReviewService(token).uploadSignedPdf(review.id, fd);
      toast.success('Signed annual review uploaded. Pay raise request sent if applicable.');
      onUploaded(res.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not upload signed PDF');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 style={{ ...layout.title, marginBottom: 4 }}>Upload Signed Annual Review</h1>
      <p style={layout.subtitle as any}>
        Download the draft combined PDF, print it, have both the director and {review.employeeName} sign it, and upload
        the signed copy here.
      </p>

      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          {review.draftCombinedPdfUrl ? (
            <a
              href={review.draftCombinedPdfUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                color: colors.accent,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              <i className="fas fa-download" /> Download draft combined PDF
            </a>
          ) : (
            <p style={{ color: colors.muted, margin: 0 }}>Draft PDF not available yet.</p>
          )}
          <button
            type="button"
            onClick={regenerate}
            disabled={regenerating}
            style={{ ...secondaryAction, cursor: regenerating ? 'wait' : 'pointer' }}
            title="Regenerates the draft PDF using the latest data and template"
          >
            <i className={`fas ${regenerating ? 'fa-spinner fa-spin' : 'fa-sync-alt'} mr-1`} />
            {regenerating ? 'Regenerating…' : 'Regenerate Draft PDF'}
          </button>
          <button
            type="button"
            onClick={onEditGrowthPlan}
            style={{ ...secondaryAction, cursor: 'pointer' }}
            title="Optionally add or edit the growth plan, then regenerate the report to include it"
          >
            <i className="fas fa-seedling mr-1" />
            {review.growthPlan?.professionalGoal ? 'Edit Growth Plan' : 'Add Growth Plan'}
          </button>
        </div>

        <label style={layout.label as any}>Signed PDF</label>
        <FileInput
          accept="application/pdf"
          selected={file}
          onChange={setFile}
          placeholder="Drop the signed PDF here or click to browse"
          hint="Accepted format: PDF"
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <button type="button" onClick={onClose} style={layout.secondaryBtn}>
          Cancel
        </button>
        <button
          type="button"
          onClick={upload}
          disabled={submitting || !file}
          style={file && !submitting ? layout.primaryBtn : layout.disabledBtn}
        >
          {submitting ? 'Uploading…' : 'Upload Signed PDF'}
        </button>
      </div>
    </div>
  );
};

export default UploadSignedModal;
